import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { LoadingLabel } from "@/components/ui/loading-label";
import { LocalDate } from "@/components/ui/local-date";
import { toast } from "@/components/ui/use-toast";
import { ApiError, api } from "@/lib/api";
import { useApiMutation } from "@/lib/use-api-mutation";
import { Link, createFileRoute, redirect, useNavigate, useRouter } from "@tanstack/react-router";
import { useState } from "react";

/**
 * `/customers/:id/edit` — accountant/admin-only. The customer's
 * editable settings (name / fakturoidSubjectId) plus the danger-zone
 * delete, split off from the read-only customer profile at
 * `/customers/:id`. Since the 2026-05-20 Customer ↔ GitLab decouple
 * customers are Hub-only — no GitLab group path lives here anymore.
 */

type CustomerEdit = {
  id: string;
  name: string;
  fakturoidSubjectId: string | null;
  createdAt: string;
};

export const Route = createFileRoute("/_app/customers/$id/edit")({
  loader: async ({ params }) => {
    try {
      return await api.get<CustomerEdit>(`/api/customers/${params.id}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        throw redirect({ to: "/my" });
      }
      throw err;
    }
  },
  component: CustomerEditPage,
});

const fieldClass =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

function CustomerEditPage() {
  const customer = Route.useLoaderData();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Edit {customer.name}</h1>
          <p className="text-muted-foreground mt-1">Customer settings</p>
        </div>
        <Button variant="outline" asChild>
          <Link to="/customers/$id" params={{ id: customer.id }}>
            Back to profile
          </Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Customer information</CardTitle>
          <CardDescription>Update the customer's name and integration bindings.</CardDescription>
        </CardHeader>
        <CardContent>
          <EditCustomerForm customer={customer} />
        </CardContent>
      </Card>

      <Card className="border-destructive">
        <CardHeader>
          <CardTitle className="text-destructive">Danger Zone</CardTitle>
          <CardDescription>
            Permanently delete this customer and all associated data.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DeleteCustomerForm customerId={customer.id} />
        </CardContent>
      </Card>
    </div>
  );
}

function EditCustomerForm({ customer }: { customer: CustomerEdit }) {
  const router = useRouter();
  const navigate = useNavigate();
  const [name, setName] = useState(customer.name);
  const [fakturoidSubjectId, setFakturoidSubjectId] = useState(customer.fakturoidSubjectId ?? "");
  const save = useApiMutation(
    (body: { name: string; fakturoidSubjectId: string | undefined }) =>
      api.patch(`/api/customers/${customer.id}`, body),
    {
      onSuccess: () => {
        toast({ title: "Customer updated", variant: "success" });
        router.invalidate();
        navigate({ to: "/customers/$id", params: { id: customer.id } });
      },
      onError: (msg) => toast({ title: "Update failed", description: msg, variant: "destructive" }),
    },
  );

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (save.pending) return;
    if (!name.trim()) {
      toast({ title: "Name is required", variant: "destructive" });
      return;
    }
    await save.mutate({
      name: name.trim(),
      fakturoidSubjectId: fakturoidSubjectId.trim() || undefined,
    });
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="space-y-2">
        <label htmlFor="name" className="text-sm font-medium leading-none">
          Name <span className="text-destructive">*</span>
        </label>
        <input
          id="name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={fieldClass}
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="fakturoidSubjectId" className="text-sm font-medium leading-none">
          Fakturoid Subject ID
        </label>
        <input
          id="fakturoidSubjectId"
          type="number"
          value={fakturoidSubjectId}
          onChange={(e) => setFakturoidSubjectId(e.target.value)}
          className={fieldClass}
        />
      </div>

      <div className="flex items-center gap-3 pt-2">
        <Button type="submit" disabled={save.pending}>
          {save.pending ? <LoadingLabel text="Saving" /> : "Save Changes"}
        </Button>
        <span className="text-sm text-muted-foreground">
          Created <LocalDate value={customer.createdAt} />
        </span>
      </div>
    </form>
  );
}

function DeleteCustomerForm({ customerId }: { customerId: string }) {
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState("");
  const remove = useApiMutation(() => api.delete(`/api/customers/${customerId}`), {
    onSuccess: () => {
      toast({ title: "Customer deleted", variant: "success" });
      navigate({ to: "/customers" });
    },
    // 409 when projects still reference the customer — the server's
    // friendly "reassign first" message rides through here (the hook's
    // default extraction surfaces it).
    onError: (msg) => toast({ title: "Delete failed", description: msg, variant: "destructive" }),
  });

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (remove.pending) return;
    if (confirm !== "DELETE") {
      toast({ title: "Type DELETE to confirm", variant: "destructive" });
      return;
    }
    await remove.mutate();
  }

  return (
    <form onSubmit={submit} className="flex items-end gap-3">
      <div className="space-y-2">
        <label htmlFor="confirm" className="text-sm font-medium leading-none">
          Type DELETE to confirm
        </label>
        <input
          id="confirm"
          type="text"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder="DELETE"
          className={fieldClass}
        />
      </div>
      <Button type="submit" variant="destructive" disabled={remove.pending}>
        {remove.pending ? <LoadingLabel text="Deleting" /> : "Delete Customer"}
      </Button>
    </form>
  );
}
